import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, Observable } from 'rxjs';
import { translate } from '@jsverse/transloco';
import { switchMap, map, tap, catchError, take } from 'rxjs/operators';
import { DeviceNoteService } from '../../devices/services/note.service';
import { ICreateNoteRequest, INote, INoteDetailsResponse, IUpdateNoteRequest } from '../../devices/models/note';
import { AppNotificationService } from '../../shared/services/notification.service';

export interface DeviceState {
  deviceNotes: INoteDetailsResponse[];
}

@Injectable()
export class DeviceNoteStore extends ComponentStore<DeviceState> {

  // Initial state
  constructor(
    private deviceNoteService: DeviceNoteService,
    private appnotificationService: AppNotificationService) {
      super({ 
        deviceNotes: [], 
      });
  }

  // Selector for the list of notes
  readonly deviceNotes$: Observable<INoteDetailsResponse[]> = this.select(state => state.deviceNotes);

  // Effect to load notes by device
  readonly getNotesByDevice = this.effect<string>(deviceId$ =>
    deviceId$.pipe(
      switchMap(deviceId =>
        this.deviceNoteService.getDeviceNotesByDevice(deviceId).pipe(
          tap(deviceNotes => {
            const sortedNotes = deviceNotes.data.sort((a, b) => 
              new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
            );

            this.patchState(state => ({
              deviceNotes: [...state.deviceNotes, ...sortedNotes]
            }));
          })
        )
      ),
      take(1)
    )
  );


  // Method to add a note to store by its ID
  readonly addNote = this.effect<ICreateNoteRequest>(note$ =>
    note$.pipe(
      switchMap(note =>
        this.deviceNoteService.createDeviceNote(note).pipe(  // Fetch the note from the service
          tap(newNote => {
            // Append the new note to the existing deviceNotes array
            this.patchState(state => {
              // Sort the updated list of notes by creation date
              const updatedNotes = [...state.deviceNotes, newNote].sort((a, b) => 
                new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
              );
  
              return { deviceNotes: updatedNotes };
            });

            if (newNote) {
              this.appnotificationService.notification$.next({ message:translate('note.createSuccess'), style: 'success' });
            } else {
              this.appnotificationService.notification$.next({ message:translate('note.createFailed'), style: 'error' });
            }
          })
        )
      )
    )
  );
  

  // Effect to update a note by its ID
  readonly updateNoteById = this.effect<IUpdateNoteRequest>(note$ =>
    note$.pipe(
      switchMap(note =>
        this.deviceNoteService.updateDeviceNote(note).pipe(
          tap(updatedNote => {
            this.patchState(state => ({
              deviceNotes: state.deviceNotes.map(n =>
                n.id === updatedNote.id ? updatedNote : n
              )
            }));

            if (updatedNote) {
              this.appnotificationService.notification$.next({ message:translate('note.updateSuccess'), style: 'success' });
            } else {
              this.appnotificationService.notification$.next({ message:translate('note.updateFailed'), style: 'error' });
            }
          })
        )
      )
    )
  );

  // Method to remove a device note from store by its ID
  readonly removeNoteById = this.effect<string>(noteId$ =>
    noteId$.pipe(
      switchMap(noteId =>
        this.deviceNoteService.deleteDeviceNote(noteId).pipe(
          tap(() => {
            this.patchState(state => ({
              deviceNotes: state.deviceNotes.filter(note => note.id !== noteId)
            }));
            this.appnotificationService.notification$.next({ message:translate('note.deleteSuccess'), style: 'success' });
          }),
          catchError((error) => {
            this.appnotificationService.notification$.next({ message:translate('note.deleteFailed'), style: 'error' });
            return EMPTY;
          })
        )
      )
    )
  );

  readonly logStateChanges = this.effect<void>(trigger$ =>
    trigger$.pipe(
      tap(() => {
        this.state$.subscribe(state => console.log('ComponentStore State:', state));
      })
    )
  );
}
